import type { ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Reveal } from './Reveal';

type Ambient = 'none' | 'grid' | 'grid-faint' | 'ledger';

export type SectionFrameProps = {
  children: ReactNode;
  ambient?: Ambient;
  rule?: boolean;
  className?: string;
  id?: string;
};

const AMBIENT_CLASS: Record<Exclude<Ambient, 'none'>, string> = {
  grid: 'grid-paper',
  'grid-faint': 'grid-paper-faint',
  ledger: 'ledger-paper',
};

/**
 * Standard section shell enforcing vertical rhythm and 間.
 *
 * - Optionally lays a subtle grid/ledger ambient field behind content,
 *   masked with veil-edges, drifting transform-only (~3-4px, 12s) — no
 *   orbs. Drift is disabled under reduced motion.
 * - When `rule` is true, renders the section's opening hairline rule as
 *   a Reveal variant='drawLine' (origin-left) that draws BEFORE content.
 */
export function SectionFrame({
  children,
  ambient = 'none',
  rule = true,
  className,
  id,
}: SectionFrameProps) {
  const reduce = useReducedMotion();

  return (
    <section id={id} className={`relative ${className ?? ''}`.trim()}>
      {ambient !== 'none' && (
        <div
          aria-hidden="true"
          className={[
            'pointer-events-none absolute inset-0 -z-10 veil-edges',
            AMBIENT_CLASS[ambient],
            reduce ? '' : 'animate-drift',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}
      {rule && (
        <Reveal
          variant="drawLine"
          className="rule mb-10 origin-left"
        />
      )}
      {children}
    </section>
  );
}
